import axios from "axios";
import React, { useState } from "react";
import { ThreeDots } from "react-loader-spinner";
import { api_url } from "../../data";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useAuth } from "../../context/Auth";

const ChurchSms = ({ churchDetails }) => {
  const { requestWithAuth } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    message: "",
    recipients: "",
  });

  // HANDLE CHANGE
  const handleChange = (e) => {
    setData({
      ...data,
      [e.target.name]: e.target.value,
    });
  };

  // SUBMIT FORM
  const submitForm = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      const response = await requestWithAuth(
        "/accounts/send-sms/",
        "POST",
        data
      )
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "SMS sent successfully",
      }).then(() => {
        window.location.reload();
      });
    } catch (error) {
      
      if(error.response.data){
        toast.error(error.response.data.message)
        if(error.response.status === 400){
          for( const property in error.response.data.errors){
            toast.error(`${property} error: ${error.response.data.errors[property]}`)
          }
        }
      }else{
        toast.error("An error occurred while processing your request.");
      }
      
    }finally{
      setSubmitting(false);
    }
    
    
  };

  const messageLength = data?.message?.length || 0;
  const isOverLimit = messageLength > 160;
  const remainingSms = churchDetails?.sms_bundle || 0;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">SMS Center</h2>
        <p className="text-gray-600">
          Send bulk SMS messages to your members and visitors
        </p>
      </div>

      {/* SMS Bundle Status */}
      <div className="mb-6">
        <div className={`p-4 rounded-lg border ${remainingSms > 50 ? 'bg-green-50 border-green-200' : remainingSms > 10 ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-3 ${remainingSms > 50 ? 'bg-green-400' : remainingSms > 10 ? 'bg-yellow-400' : 'bg-red-400'}`}></div>
            <div className="flex-1">
              <p className={`text-sm font-medium ${remainingSms > 50 ? 'text-green-800' : remainingSms > 10 ? 'text-yellow-800' : 'text-red-800'}`}>
                SMS Bundle Status
              </p>
              <p className={`text-lg font-bold ${remainingSms > 50 ? 'text-green-900' : remainingSms > 10 ? 'text-yellow-900' : 'text-red-900'}`}>
                {remainingSms} SMS credits remaining
              </p>
            </div>
            <svg className={`w-8 h-8 ${remainingSms > 50 ? 'text-green-500' : remainingSms > 10 ? 'text-yellow-500' : 'text-red-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
        </div>
      </div>

      {/* SMS Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <form onSubmit={(e) => submitForm(e)} className="space-y-6">
          {/* Recipients Selection */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              Recipients
            </label>
            <div className="relative">
              <select
                name="recipients"
                onChange={(e) => handleChange(e)}
                value={data.recipients}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 appearance-none bg-white"
                required
              >
                <option value="">Select Recipients</option>
                <option value="Members">Members</option>
                <option value="Visitors">Visitors</option>
              </select>
              <svg className="absolute right-3 top-2.5 w-5 h-5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            <p className="text-sm text-gray-500">
              Choose whether to send to church members or visitors
            </p>
          </div>

          {/* Message Input */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Message Content
              </label>
              <span className={`text-sm font-medium ${isOverLimit ? 'text-red-600' : messageLength > 140 ? 'text-yellow-600' : 'text-gray-500'}`}>
                {messageLength}/160 characters
              </span>
            </div>
            <div className="relative">
              <textarea
                name="message"
                onChange={(e) => handleChange(e)}
                value={data.message}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 transition-colors duration-200 resize-none ${
                  isOverLimit 
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                rows="6"
                placeholder="Type your message here..."
                required
              />
              <div className="absolute bottom-2 right-2">
                <svg className={`w-5 h-5 ${isOverLimit ? 'text-red-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
            </div>
            
            {/* Character limit warning */}
            {isOverLimit && (
              <div className="flex items-center mt-2 text-red-600">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span className="text-sm">Message exceeds 160 character limit</span>
              </div>
            )}
            
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-sm text-blue-800 font-medium">SMS Guidelines:</p>
                  <ul className="text-sm text-blue-700 mt-1 space-y-1">
                    <li>• Keep messages under 160 characters for single SMS</li>
                    <li>• Messages over 160 characters may be split into multiple SMS</li>
                    <li>• Each SMS sent will deduct from your bundle balance</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4">
            {submitting ? (
              <div className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 cursor-not-allowed">
                <ThreeDots
                  height="20"
                  width="20"
                  radius="9"
                  color="#ffffff"
                  ariaLabel="three-dots-loading"
                  wrapperStyle={{}}
                  wrapperClassName=""
                  visible={true}
                />
                <span className="ml-2">Sending SMS...</span>
              </div>
            ) : (
              <button
                type="submit"
                disabled={isOverLimit || remainingSms <= 0}
                className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm transition-colors duration-200 ${
                  isOverLimit || remainingSms <= 0
                    ? 'text-gray-400 bg-gray-200 cursor-not-allowed'
                    : 'text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                }`}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                Send SMS
              </button>
            )}
          </div>
        </form>

        {/* Low Balance Warning */}
        {remainingSms <= 10 && remainingSms > 0 && (
          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-yellow-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-yellow-800">
                <span className="font-medium">Low SMS Balance:</span> You have {remainingSms} SMS credits remaining. Consider topping up your bundle.
              </p>
            </div>
          </div>
        )}

        {/* No Balance Warning */}
        {remainingSms <= 0 && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-red-800">
                <span className="font-medium">No SMS Credits:</span> You have no SMS credits remaining. Please top up your bundle to send messages.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChurchSms;